import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FlaskConical, Plus, Trash2, X, Copy, Check, CalendarClock, Pencil, Eye, EyeOff, History } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fdate = (iso) => iso ? new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

export function TestAccountsPanel({ theme }) {
  const light = theme === 'light';
  const [data, setData] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [expiryTarget, setExpiryTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [historyTarget, setHistoryTarget] = useState(null);
  const th = light ? 'text-gray-500' : 'text-white/40';
  const cardCls = light ? 'bg-white border-gray-200' : 'bg-white/[0.03] border-white/10';

  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/admin/test-accounts`, { withCredentials: true });
      setData(d);
    } catch { setData({ items: [], plans: [] }); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const changePlan = async (email, plan) => {
    try {
      await axios.post(`${API}/admin/test-accounts/${encodeURIComponent(email)}/plan`, { plan }, { withCredentials: true });
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Erreur lors du changement de forfait'); }
  };

  const remove = async (email) => {
    if (!window.confirm(`Supprimer le compte test ${email} ?\n\nCe compte et toutes ses données seront définitivement supprimés.`)) return;
    try {
      await axios.delete(`${API}/admin/test-accounts/${encodeURIComponent(email)}`, { withCredentials: true });
      load();
    } catch (e) { alert(e.response?.data?.detail || 'Erreur lors de la suppression'); }
  };

  return (
    <div data-testid="test-accounts-panel">
      <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className={`text-lg font-bold flex items-center gap-2 ${light ? 'text-gray-900' : 'text-white'}`}>
            <FlaskConical size={18} className="text-[#E9641A]" /> Comptes Test ({data?.items?.length ?? '…'})
          </h2>
          <p className={`text-xs mt-1 max-w-xl ${th}`}>
            Comptes avec accès gratuit complet, sans paiement Stripe. Ils permettent de tout tester sur la plateforme, y compris le changement de forfait. Supprimables à tout moment.
          </p>
        </div>
        <button onClick={() => setShowCreate(true)} data-testid="create-test-account-btn"
          className="flex items-center gap-2 px-4 py-2 bg-[#E9641A] text-white text-xs font-semibold rounded-lg hover:bg-[#FE975E] transition-colors">
          <Plus size={14} /> Créer un compte test
        </button>
      </div>

      <div className={`border rounded-xl overflow-x-auto ${cardCls}`}>
        <table className="w-full text-left" data-testid="test-accounts-table">
          <thead>
            <tr className={light ? 'border-b border-gray-200' : 'border-b border-white/10'}>
              {['Compte', 'Forfait', 'Expiration', 'Créé le', ''].map((l, i) => (
                <th key={i} className={`px-4 py-3 text-[10px] font-semibold uppercase tracking-wide whitespace-nowrap ${th}`}>{l}</th>
              ))}
            </tr>
          </thead>
          <tbody className={light ? 'divide-y divide-gray-100' : 'divide-y divide-white/5'}>
            {(data?.items || []).map((a, i) => (
              <tr key={a.email} data-testid={`test-account-row-${i}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div>
                      <p className={`text-xs font-semibold ${light ? 'text-gray-900' : 'text-white'}`}>{a.name || '—'}</p>
                      <p className={`text-[10px] ${th}`}>{a.email}</p>
                    </div>
                    <span className="px-2 py-0.5 rounded-full text-[9px] font-bold bg-[#E9641A]/15 text-[#E9641A]">TEST</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <select value={a.plan} onChange={e => changePlan(a.email, e.target.value)} data-testid={`test-account-plan-${i}`}
                    className={`text-xs rounded-lg px-2.5 py-1.5 border font-semibold focus:outline-none focus:border-[#E9641A] ${light ? 'bg-white border-gray-200 text-gray-700' : 'bg-white/5 border-white/10 text-white/80'}`}>
                    {(data?.plans || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3 text-xs whitespace-nowrap" data-testid={`test-account-expiry-${i}`}>
                  <span className="inline-flex items-center gap-1.5">
                    {a.expired ? (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-500/15 text-red-400">Expiré le {fdate(a.expires_at)}</span>
                    ) : a.expires_at ? (
                      <span className="text-amber-400 font-semibold">Expire le {fdate(a.expires_at)}</span>
                    ) : (
                      <span className={th}>Illimitée</span>
                    )}
                    <button onClick={() => setExpiryTarget(a)} title="Prolonger / modifier l'expiration" data-testid={`edit-expiry-${i}`}
                      className={`p-1 rounded transition-colors ${light ? 'text-gray-300 hover:text-[#E9641A] hover:bg-orange-50' : 'text-white/30 hover:text-[#FE975E] hover:bg-[#E9641A]/10'}`}>
                      <CalendarClock size={13} />
                    </button>
                  </span>
                </td>
                <td className={`px-4 py-3 text-xs whitespace-nowrap ${light ? 'text-gray-600' : 'text-white/60'}`}>{fdate(a.created_at)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => setHistoryTarget(a)} title="Historique d'utilisation (connexions, IP, visionnage)" data-testid={`history-test-account-${i}`}
                    className={`p-1.5 rounded transition-colors ${light ? 'text-gray-300 hover:text-[#E9641A] hover:bg-orange-50' : 'text-white/30 hover:text-[#FE975E] hover:bg-[#E9641A]/10'}`}>
                    <History size={14} />
                  </button>
                  <button onClick={() => setEditTarget(a)} title="Voir / modifier email, nom et mot de passe" data-testid={`edit-test-account-${i}`}
                    className={`p-1.5 rounded transition-colors ${light ? 'text-gray-300 hover:text-[#E9641A] hover:bg-orange-50' : 'text-white/30 hover:text-[#FE975E] hover:bg-[#E9641A]/10'}`}>
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => remove(a.email)} title="Supprimer ce compte test" data-testid={`delete-test-account-${i}`}
                    className={`p-1.5 rounded transition-colors ${light ? 'text-gray-300 hover:text-red-500 hover:bg-red-50' : 'text-white/30 hover:text-red-400 hover:bg-red-500/10'}`}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {data && data.items.length === 0 && (
              <tr><td colSpan={5} className={`px-4 py-10 text-center text-xs ${th}`}>Aucun compte test. Créez-en un pour tester la plateforme sans paiement.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showCreate && <CreateTestAccountModal light={light} plans={data?.plans || []} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
      {expiryTarget && <ExpiryModal light={light} account={expiryTarget} onClose={() => setExpiryTarget(null)} onSaved={() => { setExpiryTarget(null); load(); }} />}
      {editTarget && <EditAccountModal light={light} account={editTarget} onClose={() => setEditTarget(null)} onSaved={() => { setEditTarget(null); load(); }} />}
      {historyTarget && <HistoryModal light={light} account={historyTarget} onClose={() => setHistoryTarget(null)} />}
    </div>
  );
}

function EditAccountModal({ light, account, onClose, onSaved }) {
  const [email, setEmail] = useState(account.email);
  const [name, setName] = useState(account.name || '');
  const [password, setPassword] = useState(account.password || '');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const inputCls = `w-full px-3 py-2.5 rounded-lg text-sm border focus:outline-none focus:border-[#E9641A] ${light ? 'bg-white border-gray-200 text-gray-800' : 'bg-white/5 border-white/10 text-white'}`;
  const labelCls = `block text-[11px] font-semibold mb-1.5 ${light ? 'text-gray-600' : 'text-white/50'}`;

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await axios.post(`${API}/admin/test-accounts/${encodeURIComponent(account.email)}/update`,
        { email, name, password }, { withCredentials: true });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la modification');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="edit-test-account-modal">
      <div className={`rounded-xl border w-full max-w-md p-6 ${light ? 'bg-white border-gray-200' : 'bg-[#1a0e2e] border-white/10'}`} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className={`text-lg font-bold ${light ? 'text-gray-900' : 'text-white'}`}>Modifier le compte test</h3>
          <button onClick={onClose} className={light ? 'text-gray-400 hover:text-gray-700' : 'text-white/40 hover:text-white'} data-testid="edit-test-account-close"><X size={18} /></button>
        </div>
        <p className={`text-xs mb-4 ${light ? 'text-gray-500' : 'text-white/50'}`}>{account.email}</p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className={labelCls}>Email</label>
            <input type="email" required value={email} onChange={e => setEmail(e.target.value)} className={inputCls} data-testid="edit-email-input" />
          </div>
          <div>
            <label className={labelCls}>Nom</label>
            <input value={name} onChange={e => setName(e.target.value)} className={inputCls} data-testid="edit-name-input" />
          </div>
          <div>
            <label className={labelCls}>Mot de passe</label>
            <div className="relative">
              <input type={showPwd ? 'text' : 'password'} minLength={6} value={password} onChange={e => setPassword(e.target.value)}
                className={`${inputCls} pr-10`} data-testid="edit-password-input" />
              <button type="button" onClick={() => setShowPwd(s => !s)} data-testid="toggle-password-visibility"
                className={`absolute right-2.5 top-1/2 -translate-y-1/2 ${light ? 'text-gray-400 hover:text-gray-700' : 'text-white/40 hover:text-white'}`}>
                {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
            {!account.password && <p className={`text-[10px] mt-1 ${light ? 'text-gray-400' : 'text-white/30'}`}>Mot de passe actuel inconnu (compte créé avant cette fonctionnalité) — saisissez-en un nouveau.</p>}
          </div>
          {error && <p className="text-xs text-red-400" data-testid="edit-account-error">{error}</p>}
          <button type="submit" disabled={loading} data-testid="edit-account-save-btn"
            className="w-full py-2.5 bg-[#E9641A] text-white text-sm font-semibold rounded-lg hover:bg-[#FE975E] transition-colors disabled:opacity-60">
            {loading ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </form>
      </div>
    </div>
  );
}

function ExpiryModal({ light, account, onClose, onSaved }) {
  const [customDays, setCustomDays] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const apply = async (days) => {
    setError(''); setLoading(true);
    try {
      await axios.post(`${API}/admin/test-accounts/${encodeURIComponent(account.email)}/expiry`, { expires_days: days }, { withCredentials: true });
      onSaved();
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur lors de la modification');
      setLoading(false);
    }
  };

  const quickBtn = `px-3 py-2 text-xs font-semibold rounded-lg transition-colors disabled:opacity-50 ${light ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' : 'bg-white/10 text-white/80 hover:bg-white/20'}`;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="expiry-modal">
      <div className={`rounded-xl border w-full max-w-sm p-6 ${light ? 'bg-white border-gray-200' : 'bg-[#1a0e2e] border-white/10'}`} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className={`text-base font-bold ${light ? 'text-gray-900' : 'text-white'}`}>Expiration du compte test</h3>
          <button onClick={onClose} className={light ? 'text-gray-400 hover:text-gray-700' : 'text-white/40 hover:text-white'} data-testid="expiry-modal-close"><X size={18} /></button>
        </div>
        <p className={`text-xs mb-4 ${light ? 'text-gray-500' : 'text-white/50'}`}>{account.email}{account.expired ? ' — la prolongation réactivera l\'accès.' : ''}</p>
        <div className="grid grid-cols-2 gap-2 mb-3">
          <button disabled={loading} onClick={() => apply(7)} className={quickBtn} data-testid="expiry-plus-7">+7 jours</button>
          <button disabled={loading} onClick={() => apply(30)} className={quickBtn} data-testid="expiry-plus-30">+30 jours</button>
          <button disabled={loading} onClick={() => apply(90)} className={quickBtn} data-testid="expiry-plus-90">+90 jours</button>
          <button disabled={loading} onClick={() => apply(null)} className={quickBtn} data-testid="expiry-unlimited">Illimitée</button>
        </div>
        <div className="flex gap-2">
          <input type="number" min="1" value={customDays} onChange={e => setCustomDays(e.target.value)} placeholder="Nombre de jours…"
            className={`flex-1 px-3 py-2 rounded-lg text-xs border focus:outline-none focus:border-[#E9641A] ${light ? 'bg-white border-gray-200 text-gray-800' : 'bg-white/5 border-white/10 text-white'}`}
            data-testid="expiry-custom-input" />
          <button disabled={loading || !customDays} onClick={() => apply(parseInt(customDays, 10))} data-testid="expiry-custom-apply"
            className="px-4 py-2 bg-[#E9641A] text-white text-xs font-semibold rounded-lg hover:bg-[#FE975E] transition-colors disabled:opacity-50">Appliquer</button>
        </div>
        <p className={`text-[10px] mt-2 ${light ? 'text-gray-400' : 'text-white/30'}`}>La durée est comptée à partir de maintenant.</p>
        {error && <p className="text-xs text-red-400 mt-2" data-testid="expiry-error">{error}</p>}
      </div>
    </div>
  );
}

function CreateTestAccountModal({ light, plans, onClose, onCreated }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [plan, setPlan] = useState(plans[0]?.id || 'premium');
  const [expiresDays, setExpiresDays] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);
  const inputCls = `w-full px-3 py-2.5 rounded-lg text-sm border focus:outline-none focus:border-[#E9641A] ${light ? 'bg-white border-gray-200 text-gray-800' : 'bg-white/5 border-white/10 text-white'}`;
  const labelCls = `block text-[11px] font-semibold mb-1.5 ${light ? 'text-gray-600' : 'text-white/50'}`;

  const genPassword = () => {
    const chars = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789';
    setPassword(Array.from({ length: 10 }, () => chars[Math.floor(Math.random() * chars.length)]).join(''));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const body = { email, password, name, plan };
      if (expiresDays) body.expires_days = parseInt(expiresDays, 10);
      await axios.post(`${API}/admin/test-accounts`, body, { withCredentials: true });
      setCreated({ email: email.trim().toLowerCase(), password });
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la création du compte');
    }
    setLoading(false);
  };

  const copyCreds = () => {
    navigator.clipboard.writeText(`Email : ${created.email}\nMot de passe : ${created.password}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="create-test-account-modal">
      <div className={`rounded-xl border w-full max-w-md p-6 ${light ? 'bg-white border-gray-200' : 'bg-[#1a0e2e] border-white/10'}`} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className={`text-lg font-bold ${light ? 'text-gray-900' : 'text-white'}`}>{created ? 'Compte test créé' : 'Créer un compte test'}</h3>
          <button onClick={created ? onCreated : onClose} className={light ? 'text-gray-400 hover:text-gray-700' : 'text-white/40 hover:text-white'} data-testid="create-test-account-close"><X size={18} /></button>
        </div>

        {created ? (
          <div data-testid="test-account-created-view">
            <p className={`text-xs mb-4 ${light ? 'text-gray-500' : 'text-white/50'}`}>Le compte a un accès complet gratuit. Notez les identifiants ci-dessous :</p>
            <div className={`rounded-lg border p-4 mb-4 space-y-2 ${light ? 'bg-gray-50 border-gray-200' : 'bg-white/[0.03] border-white/10'}`}>
              <p className={`text-sm ${light ? 'text-gray-800' : 'text-white'}`}><span className={`text-[11px] ${light ? 'text-gray-500' : 'text-white/40'}`}>Email : </span><span className="font-semibold" data-testid="created-email">{created.email}</span></p>
              <p className={`text-sm ${light ? 'text-gray-800' : 'text-white'}`}><span className={`text-[11px] ${light ? 'text-gray-500' : 'text-white/40'}`}>Mot de passe : </span><span className="font-mono font-semibold">{created.password}</span></p>
            </div>
            <div className="flex gap-2">
              <button onClick={copyCreds} data-testid="copy-credentials-btn"
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-xs font-semibold rounded-lg transition-colors ${light ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}>
                {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} />} {copied ? 'Copié !' : 'Copier les identifiants'}
              </button>
              <button onClick={onCreated} data-testid="test-account-done-btn"
                className="flex-1 px-4 py-2.5 bg-[#E9641A] text-white text-xs font-semibold rounded-lg hover:bg-[#FE975E] transition-colors">Terminé</button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 mt-4" data-testid="create-test-account-form">
            <div>
              <label className={labelCls}>Email</label>
              <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="test@mayaplus.fr" className={inputCls} data-testid="test-account-email-input" />
            </div>
            <div>
              <label className={labelCls}>Nom (optionnel)</label>
              <input value={name} onChange={e => setName(e.target.value)} placeholder="Testeur" className={inputCls} data-testid="test-account-name-input" />
            </div>
            <div>
              <label className={labelCls}>Mot de passe (6 caractères min.)</label>
              <div className="flex gap-2">
                <input required minLength={6} value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" className={inputCls} data-testid="test-account-password-input" />
                <button type="button" onClick={genPassword} data-testid="generate-password-btn"
                  className={`px-3 py-2 text-[11px] font-semibold rounded-lg whitespace-nowrap transition-colors ${light ? 'bg-gray-100 text-gray-600 hover:bg-gray-200' : 'bg-white/10 text-white/70 hover:bg-white/20'}`}>Générer</button>
              </div>
            </div>
            <div>
              <label className={labelCls}>Forfait initial</label>
              <select value={plan} onChange={e => setPlan(e.target.value)} className={inputCls} data-testid="test-account-plan-select">
                {plans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Durée de vie (jours, optionnel)</label>
              <input type="number" min="1" value={expiresDays} onChange={e => setExpiresDays(e.target.value)}
                placeholder="Vide = illimitée" className={inputCls} data-testid="test-account-expires-input" />
              <p className={`text-[10px] mt-1 ${light ? 'text-gray-400' : 'text-white/30'}`}>Le compte se désactivera automatiquement après ce délai.</p>
            </div>
            {error && <p className="text-xs text-red-400" data-testid="test-account-error">{error}</p>}
            <button type="submit" disabled={loading} data-testid="test-account-submit-btn"
              className="w-full py-2.5 bg-[#E9641A] text-white text-sm font-semibold rounded-lg hover:bg-[#FE975E] transition-colors disabled:opacity-60">
              {loading ? 'Création…' : 'Créer le compte test'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

const fdt = (iso) => iso ? new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';
const fdur = (s) => s >= 3600 ? `${Math.floor(s / 3600)}h${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}` : `${Math.floor(s / 60)} min`;

function HistoryModal({ light, account, onClose }) {
  const [h, setH] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    axios.get(`${API}/admin/test-accounts/${encodeURIComponent(account.email)}/history`, { withCredentials: true })
      .then(r => setH(r.data)).catch(() => setErr('Impossible de charger l\'historique'));
  }, [account.email]);
  const sec = light ? 'text-gray-500' : 'text-white/45';
  const txt = light ? 'text-gray-900' : 'text-white';
  const box = light ? 'bg-gray-50 border border-gray-200' : 'bg-white/5';
  const Head = ({ children }) => <h4 className={`text-xs font-bold uppercase tracking-wide mt-5 mb-2 ${sec}`}>{children}</h4>;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose} data-testid="test-account-history-modal">
      <div className={`w-full max-w-3xl max-h-[85vh] overflow-y-auto rounded-xl p-6 ${light ? 'bg-white' : 'bg-[#1d0f30]'}`} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className={`font-bold text-lg ${txt}`}>Historique — {account.email}</h3>
          <button onClick={onClose} className={sec} data-testid="history-close-btn"><X size={18} /></button>
        </div>
        {err && <p className="text-red-400 text-sm">{err}</p>}
        {!h && !err && <p className={`text-sm ${sec}`}>Chargement…</p>}
        {h && (
          <div className="text-sm">
            <div className={`rounded-lg px-4 py-3 mt-3 ${box}`}>
              <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${h.used ? 'bg-emerald-500/15 text-emerald-400' : (light ? 'bg-gray-200 text-gray-500' : 'bg-white/10 text-white/50')}`} data-testid="history-used-badge">
                {h.used ? 'Compte utilisé' : 'Jamais utilisé'}
              </span>
              <span className={`ml-3 ${sec}`}>Dernière connexion : <b className={txt}>{fdt(h.last_login_at)}</b></span>
              <span className={`ml-3 ${sec}`}>Créé : {fdt(h.created_at)}</span>
            </div>
            <Head>Connexions ({h.logins.length})</Head>
            {h.logins.length === 0 ? <p className={sec}>Aucune connexion enregistrée</p> : (
              <table className="w-full text-xs">
                <thead><tr className={sec}>{['Appareil / Support', 'Localisation', 'IP', '1ʳᵉ fois', 'Dernière fois'].map(x => <th key={x} className="text-left py-1 pr-3">{x}</th>)}</tr></thead>
                <tbody>{h.logins.map((l, i) => (
                  <tr key={i} className={light ? 'border-t border-gray-100' : 'border-t border-white/5'}>
                    <td className={`py-1.5 pr-3 ${txt}`}>{l.device || '—'}</td>
                    <td className={`py-1.5 pr-3 ${sec}`}>{l.location || '—'}</td>
                    <td className={`py-1.5 pr-3 font-mono ${sec}`}>{l.ip || '—'}</td>
                    <td className={`py-1.5 pr-3 ${sec}`}>{fdt(l.first_seen)}</td>
                    <td className={`py-1.5 ${sec}`}>{fdt(l.last_seen)}</td>
                  </tr>))}</tbody>
              </table>
            )}
            <Head>Sessions de navigation ({h.sessions.length})</Head>
            {h.sessions.length === 0 ? <p className={sec}>Aucune session</p> : (
              <table className="w-full text-xs">
                <thead><tr className={sec}>{['Début', 'Support', 'Ville / IP', 'Profil', 'Visionnage', 'Navigation', 'Contenus consultés'].map(x => <th key={x} className="text-left py-1 pr-3">{x}</th>)}</tr></thead>
                <tbody>{h.sessions.map((s, i) => (
                  <tr key={i} className={light ? 'border-t border-gray-100' : 'border-t border-white/5'}>
                    <td className={`py-1.5 pr-3 whitespace-nowrap ${txt}`}>{fdt(s.started_at)}</td>
                    <td className={`py-1.5 pr-3 ${sec}`}>{s.device || '—'}</td>
                    <td className={`py-1.5 pr-3 ${sec}`}>{s.city || '—'}{s.ip ? ` · ${s.ip}` : ''}</td>
                    <td className={`py-1.5 pr-3 ${sec}`}>{s.profile || '—'}</td>
                    <td className={`py-1.5 pr-3 ${sec}`}>{fdur(s.watch_seconds)}</td>
                    <td className={`py-1.5 pr-3 ${sec}`}>{s.nav_count} pages</td>
                    <td className={`py-1.5 ${sec}`}>{(s.pages || []).join(', ') || '—'}</td>
                  </tr>))}</tbody>
              </table>
            )}
            <Head>Historique de visionnage ({h.viewing.length})</Head>
            {h.viewing.length === 0 ? <p className={sec}>Aucun visionnage</p> : (
              <table className="w-full text-xs">
                <thead><tr className={sec}>{['Contenu', 'Progression', 'Dernier visionnage'].map(x => <th key={x} className="text-left py-1 pr-3">{x}</th>)}</tr></thead>
                <tbody>{h.viewing.map((v, i) => (
                  <tr key={i} className={light ? 'border-t border-gray-100' : 'border-t border-white/5'}>
                    <td className={`py-1.5 pr-3 ${txt}`}>{v.title}{v.episode_id ? ' (épisode)' : ''}</td>
                    <td className={`py-1.5 pr-3 ${sec}`}>{Math.round(v.progress || 0)} %</td>
                    <td className={`py-1.5 ${sec}`}>{fdt(v.updated_at)}</td>
                  </tr>))}</tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
